import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatApiError } from "@/lib/api";

export const TYPE_STYLES = {
  Demo: { chip: "bg-indigo-50 text-indigo-700 border-indigo-200", bar: "border-l-indigo-500", dot: "bg-indigo-500" },
  AMC: { chip: "bg-emerald-50 text-emerald-700 border-emerald-200", bar: "border-l-emerald-500", dot: "bg-emerald-500" },
  Maintenance: { chip: "bg-amber-50 text-amber-800 border-amber-200", bar: "border-l-amber-500", dot: "bg-amber-500" },
};
export const STATUS_STYLES = {
  Scheduled: "bg-slate-100 text-slate-700",
  "In Progress": "bg-sky-100 text-sky-800",
  Completed: "bg-emerald-100 text-emerald-800",
  Cancelled: "bg-rose-100 text-rose-700 line-through",
};

const pad = (n) => String(n).padStart(2, "0");
export const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const hm = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
export const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
export const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
export const startOfWeek = (d) => { const x = startOfDay(d); const day = (x.getDay() + 6) % 7; return addDays(x, -day); };
export const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
export const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");
export const fmtDay = (d) => d.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
export const sameDay = (a, b) => ymd(a) === ymd(b);

export function rangeFor(view, anchor) {
  if (view === "day") { const s = startOfDay(anchor); return [s, addDays(s, 1)]; }
  if (view === "week") { const s = startOfWeek(anchor); return [s, addDays(s, 7)]; }
  const s = startOfWeek(startOfMonth(anchor)); return [s, addDays(s, 42)];
}

export function eventsOnDay(events, day) {
  const s = startOfDay(day).getTime(); const e = addDays(startOfDay(day), 1).getTime();
  return events.filter((ev) => new Date(ev.start).getTime() < e && new Date(ev.end).getTime() > s);
}

// Move an event to a new day (keeping its time of day and duration), optionally to a new hour.
export function shiftedSlot(ev, day, hour = null) {
  const s = new Date(ev.start); const e = new Date(ev.end); const dur = e - s;
  const ns = new Date(day); ns.setHours(hour ?? s.getHours(), hour == null ? s.getMinutes() : 0, 0, 0);
  return { start: ns.toISOString(), end: new Date(ns.getTime() + dur).toISOString() };
}

// Runs an API call; on an engineer clash (409) asks for confirmation and retries with allow_clash.
export function useClashGuard() {
  const [pending, setPending] = useState(null);
  const run = async (fn) => {
    try { return await fn(false); } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === "engineer_clash") {
        return new Promise((resolve) => setPending({ clashes: d.clashes || [], fn, resolve }));
      }
      toast.error(formatApiError(e));
      return null;
    }
  };
  const close = (val) => { pending?.resolve(val); setPending(null); };
  const confirm = async () => {
    const p = pending; setPending(null);
    try { p.resolve(await p.fn(true)); } catch (e) { toast.error(formatApiError(e)); p.resolve(null); }
  };
  const dialog = <ClashDialog data={pending} onCancel={() => close(null)} onConfirm={confirm} />;
  return { run, dialog };
}

function ClashDialog({ data, onCancel, onConfirm }) {
  return (
    <Dialog open={!!data} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-md" data-testid="clash-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="w-5 h-5" /> Engineer already booked
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">This slot overlaps with:</p>
        <ul className="space-y-2" data-testid="clash-list">
          {(data?.clashes || []).map((c) => (
            <li key={c.id} className="border border-amber-200 bg-amber-50 rounded-md px-3 py-2 text-sm" data-testid={`clash-item-${c.id}`}>
              <div className="font-semibold text-slate-900">{c.engineer_name} · {c.type}</div>
              <div className="text-slate-700">{c.company_name || "—"}</div>
              <div className="text-xs text-slate-500">
                {new Date(c.start).toLocaleDateString([], { day: "numeric", month: "short" })} · {fmtTime(c.start)} – {fmtTime(c.end)} · {c.status}
              </div>
            </li>
          ))}
        </ul>
        <p className="text-xs text-slate-500">Double-booking is sometimes intentional. Continue anyway?</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="clash-cancel-btn">Go back</Button>
          <Button className="bg-amber-600 hover:bg-amber-700" onClick={onConfirm} data-testid="clash-override-btn">Book anyway</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
